import { Injectable } from '@angular/core';
import {
  BlindingLevel,
  Category,
  CsvDelimiter,
  DataExportHttpService,
  ExportDataFormat,
  FileResponse,
  WideFormatHeaderType,
} from '@app/core/services/api-client.generated';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs';
import {
  dataExportComponentActions,
  dataExportServiceActions,
} from './data-export.actions';
import { saveBlob } from '@app/shared/utils/download-file';
import { Observable, of } from 'rxjs';
import { dataExportJobSchema } from '@core/state/entities/data-export-job/data-export-job.entity';
import { normalize } from 'normalizr';

export enum ExportType {
  bibliographic,
  screening,
  annotation,
  quantitative,
}

const capitalizeFirstLetter = (string) =>
  string.charAt(0).toUpperCase() + string.slice(1);
const dataExportJobDtoSchema = { dataExportJob: dataExportJobSchema };
export type DataExportFileInfo = {
  projectId: string;
  dataFormat: ExportDataFormat | null;
  blindingLevel: BlindingLevel | null;
  exportType: ExportType;
  category: Category | null;
  delimiter: CsvDelimiter | null;
};

export function getExportFileExtension(delimiter: CsvDelimiter | null): string {
  return delimiter === CsvDelimiter.Tab ? '.tsv' : '.csv';
}

export function getExportMimeType(delimiter: CsvDelimiter | null): string {
  return delimiter === CsvDelimiter.Tab ? 'text/tab-separated-values' : 'text/csv';
}

export function buildExportFileName(fileInfo: DataExportFileInfo): string {
  const { projectId, dataFormat, blindingLevel, exportType, category, delimiter } = fileInfo;
  const extension = getExportFileExtension(delimiter);
  const date = new Date();
  const dateString = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0];
  return `${capitalizeFirstLetter(
    ExportType[exportType]
  )} data - ${dateString.replaceAll('-', '_')} - ${
    dataFormat === ExportDataFormat.LongScreening ||
    dataFormat === ExportDataFormat.Long
      ? 'Long format - '
      : dataFormat === ExportDataFormat.OutcomeData
        ? ''
        : 'Wide format - '
  }${projectId} - Investigators ${
    BlindingLevel[blindingLevel ?? BlindingLevel.Unblinded]
  }${category ? ' - ' + Category[category] + 'level' : ''}${extension}`.replaceAll(
    ' ',
    '_'
  );
}

export const downloadFile = (
  fileResponse: FileResponse,
  fileInfo: DataExportFileInfo
) => {
  const fileName = buildExportFileName(fileInfo);
  const mimeType = getExportMimeType(fileInfo.delimiter);
  saveBlob(fileResponse.data as any, fileName, mimeType);
};

@Injectable({ providedIn: 'root' })
export class DataExportEffects {
  dataExportJobCreated = createEffect(() =>
    this._actions$.pipe(
      ofType(dataExportComponentActions.createDataExportJob),
      exhaustMap((props) =>
        this._dataExportService
          .createDataExportJob(props.projectId, props)
          .pipe(
            map((dataExportJobCreatedDto) =>
              dataExportServiceActions.dataExportJobCreated({
                jobId: dataExportJobCreatedDto.jobId,
                correlationId: props.correlationId,
                fileInfo: {
                  projectId: props.projectId,
                  dataFormat: props.dataFormat,
                  blindingLevel: props.blindingLevel,
                  exportType: props.exportType,
                  category: props.category,
                  delimiter: props.delimiter ?? null,
                },
                ...normalize(dataExportJobCreatedDto, dataExportJobDtoSchema),
              })
            ),
            catchError((error) =>
              of(
                dataExportServiceActions.errorCreatingDataExportJob({
                  error,
                  correlationId: props.correlationId,
                })
              )
            )
          )
      )
    )
  );

  dataExportReceived$ = createEffect(() =>
    this._actions$.pipe(
      ofType(dataExportServiceActions.dataExportJobCreated),
      exhaustMap((props) =>
        this._dataExportService
          .getDataExport(props.fileInfo.projectId, props.jobId, props.fileInfo.delimiter)
          .pipe(
            tap((fileResponse: FileResponse) =>
              fileResponse ? downloadFile(fileResponse, props.fileInfo) : null
            ),
            map((_) =>
              dataExportServiceActions.dataExportReceivedSuccessfully({
                jobId: props.jobId,
                correlationId: props.correlationId,
              })
            ),
            catchError((error) =>
              of(
                dataExportServiceActions.errorReceivingDataExport({
                  jobId: props.jobId,
                  error,
                  correlationId: props.correlationId,
                })
              )
            )
          )
      )
    )
  );

  constructor(
    private _actions$: Actions,
    private _dataExportService: DataExportHttpService
  ) {}
}
